import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@foundation/utils/supabaseClient';
import { User } from '@supabase/supabase-js';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import {
  generateRecaptchaToken,
  isRecaptchaConfigured,
  loadRecaptchaScript,
} from '@foundation/utils/recaptcha';
import { Button } from '@foundation/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@foundation/components/ui/card';
import { Input } from '@foundation/components/ui/input';
import { Label } from '@foundation/components/ui/label';
import { Separator } from '@foundation/components/ui/separator';
import { MagicLinkButton } from './MagicLinkButton';

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');

  useEffect(() => {
    if (inviteCode) {
      localStorage.setItem('pendingInviteCode', inviteCode);
    }
  }, [inviteCode]);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false);

  useEffect(() => {
    const enabled = isRecaptchaConfigured();
    setRecaptchaEnabled(enabled);

    if (enabled) {
      loadRecaptchaScript().catch(error => {
        console.error('Failed to load reCAPTCHA script:', error);
      });
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event: string, session: any) => {
      if (event === 'SIGNED_IN' && session?.user) {
        if (
          session.user.app_metadata.provider &&
          session.user.app_metadata.provider !== 'email'
        ) {
          await updateUserProfile(session.user);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const updateUserProfile = async (user: User) => {
    try {
      console.log(`User ${user.id} signed in with social login`);
      navigate('/');
    } catch (err) {
      console.error('Error during social login:', err);
      setError('Social login failed. Please try again.');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let recaptchaToken = '';
      if (recaptchaEnabled) {
        try {
          recaptchaToken = await generateRecaptchaToken('signup');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA token generation failed:', recaptchaError);
        }
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Signup failed');

      // If session is null, email confirmation is required before login
      if (!authData.session) {
        sessionStorage.setItem('authSuccessMessage', 'Registration successful! Please check your email to verify your account.');
        navigate('/login');
        return;
      }

      navigate('/onboarding', {
        state: {
          message: 'Account created! Please complete your profile.',
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const passwordsMatch = formData.password === formData.confirmPassword;
  const showPasswordMismatch = formData.confirmPassword && !passwordsMatch;

  return (
    <div className='min-h-screen flex flex-col items-center justify-center bg-background p-4'>
      <Card className='w-full max-w-md'>
        <CardHeader className='text-center space-y-2'>
          {/* Revisr logo — overrides the default AltZero favicon */}
          <img
            src='/favicon.png'
            alt='Revisr Logo'
            className='w-12 h-12 mx-auto object-contain'
          />
          <CardTitle className='text-2xl'>Create your account</CardTitle>
          <CardDescription>
            Join us today and start your journey
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {error && (
            <div className='p-4 rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm'>
              {error}
            </div>
          )}

          <form className='space-y-4' onSubmit={handleSubmit}>
            <div className='space-y-2'>
              <Label htmlFor='fullName'>Full Name</Label>
              <Input
                id='fullName'
                type='text'
                placeholder='John Doe'
                required
                value={formData.fullName}
                onChange={e =>
                  setFormData({ ...formData, fullName: e.target.value })
                }
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='email'>Email address</Label>
              <Input
                id='email'
                type='email'
                placeholder='name@example.com'
                required
                value={formData.email}
                onChange={e =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='password'>Password</Label>
              <div className='relative'>
                <Input
                  id='password'
                  type={showPassword ? 'text' : 'password'}
                  placeholder='••••••••'
                  required
                  value={formData.password}
                  onChange={e =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='absolute right-0 top-0 h-full'
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className='h-4 w-4' />
                  ) : (
                    <Eye className='h-4 w-4' />
                  )}
                </Button>
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='confirmPassword'>Confirm Password</Label>
              <div className='relative'>
                <Input
                  id='confirmPassword'
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder='••••••••'
                  required
                  value={formData.confirmPassword}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                />
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='absolute right-0 top-0 h-full'
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className='h-4 w-4' />
                  ) : (
                    <Eye className='h-4 w-4' />
                  )}
                </Button>
              </div>
              {showPasswordMismatch && (
                <p className='text-sm text-destructive'>
                  Passwords do not match
                </p>
              )}
            </div>

            <div className='text-xs text-center text-muted-foreground'>
              By signing up, you agree to our{' '}
              <Link to='/terms' className='underline hover:text-primary'>
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                to='/private-policy'
                className='underline hover:text-primary'
              >
                Privacy Policy
              </Link>
            </div>

            <Button
              type='submit'
              className='w-full'
              disabled={loading || !!showPasswordMismatch}
            >
              {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>

            <MagicLinkButton
              disabled={loading}
              userData={{ full_name: formData.fullName, flow: "signup" }}
              buttonText="Email me a magic signup link"
              modalTitle="Sign up with a magic link"
              tooltipText="A magic signup link lets you create your account without a password. We'll email you a secure link — click it on this device to sign up and sign in instantly."
            />
          </form>

          <div className='relative my-4'>
            <div className='absolute inset-0 flex items-center'>
              <Separator />
            </div>
            <div className='relative flex justify-center'>
              <span className='px-2 bg-card text-muted-foreground text-sm'>
                Or continue with
              </span>
            </div>
          </div>

          <Button
            type='button'
            variant='outline'
            className='w-full'
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <svg
              className='h-4 w-4 mr-2'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g transform='matrix(1, 0, 0, 1, 27.009001, -39.238998)'>
                <path
                  fill='#4285F4'
                  d='M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z'
                />
                <path
                  fill='#34A853'
                  d='M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z'
                />
                <path
                  fill='#FBBC05'
                  d='M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z'
                />
                <path
                  fill='#EA4335'
                  d='M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z'
                />
              </g>
            </svg>
            Sign up with Google
          </Button>
        </CardContent>
        <CardFooter className='flex flex-col space-y-4'>
          <div className='relative w-full'>
            <div className='absolute inset-0 flex items-center'>
              <Separator />
            </div>
            <div className='relative flex justify-center'>
              <span className='px-2 bg-card text-muted-foreground text-sm'>
                Already have an account?
              </span>
            </div>
          </div>

          <Button
            variant='outline'
            className='w-full border-primary text-primary'
            asChild
          >
            <Link to='/login'>Sign in instead</Link>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default Signup;
